/**
 * Help Scout article IDs for Beacon suggestions.
 *
 * To populate this file after uploading docs, run:
 *   node docs/upload-to-helpscout.mjs --api-key YOUR_KEY --print-ids
 *
 * Then paste the printed ARTICLE_IDS block here and fill in ROUTE_ARTICLES below.
 */

// Paste the output of --print-ids here once you have your article IDs:
const ARTICLE_IDS: Record<string, string> = {
  automations: '6999f2527dc7f17e59c17672',
  billing: '6999f2537dc7f17e59c17675',
  'branch-sections': '6999f251a5e6c1229798ee7a',
  branding: '6999f253a5e6c1229798ee7e',
  'checkin-sections': '6999f2517dc7f17e59c17671',
  'church-settings': '6999f253a5e6c1229798ee7f',
  classes: '6999e66f7dc7f17e59c1753f',
  'completion-sections': '6999f251a5e6c1229798ee7b',
  'form-sections': '6999f250a5e6c1229798ee79',
  'getting-started': '6999e6277dc7f17e59c17529',
  'guest-experience': '6999f2547dc7f17e59c17676',
  guests: '6999f2527dc7f17e59c17673',
  ministries: '6999f2527dc7f17e59c17674',
  'onark-support-index': '6999f24f7dc7f17e59c1766b',
  profile: '6999f253a5e6c1229798ee80',
  'reports-analytics': '6999f252a5e6c1229798ee7c',
  'sections-overview': '6999f2507dc7f17e59c1766d',
  'team-management': '6999f252a5e6c1229798ee7d',
  'test-sections': '6999f2517dc7f17e59c17670',
  'text-sections': '6999f2507dc7f17e59c1766e',
  'video-sections': '6999f2507dc7f17e59c1766f',
};

const a = ARTICLE_IDS;

/**
 * Section builder pages: /admin/classes/[courseId]/[sectionId]/Video etc.
 * The last path segment is the section type name.
 */
const SECTION_TYPE_ARTICLES: Record<string, string[]> = {
  Branch: [a['branch-sections'], a['sections-overview'], a.automations],
  Checkin: [
    a['checkin-sections'],
    a['sections-overview'],
    a['guest-experience'],
  ],
  Completion: [
    a['completion-sections'],
    a['sections-overview'],
    a['guest-experience'],
  ],
  Form: [a['form-sections'], a['sections-overview'], a.guests],
  Test: [a['test-sections'], a['sections-overview'], a.ministries],
  Text: [a['text-sections'], a['sections-overview'], a.classes],
  Video: [a['video-sections'], a['sections-overview'], a.classes],
};

/**
 * Maps admin route prefixes to ordered article suggestions.
 * Up to 5 articles per route (Help Scout Beacon limit is 10 but 3-5 is ideal).
 * More specific routes are listed first — the hook matches on the longest prefix.
 */
const ROUTE_ARTICLES: { prefix: string; articles: string[] }[] = [
  // Classes list & course detail
  {
    articles: [
      a.classes,
      a['sections-overview'],
      a['checkin-sections'],
      a['completion-sections'],
      a['video-sections'],
    ],
    prefix: '/admin/classes',
  },

  // Guests & Reports
  {
    articles: [
      a.guests,
      a['reports-analytics'],
      a['form-sections'],
      a['test-sections'],
    ],
    prefix: '/admin/guests',
  },
  {
    articles: [
      a['reports-analytics'],
      a.guests,
      a.automations,
      a['test-sections'],
    ],
    prefix: '/admin/reports',
  },
  {
    articles: [a['test-sections'], a.guests, a.ministries],
    prefix: '/admin/test-results',
  },

  // Church Management
  {
    articles: [a.ministries, a['test-sections'], a['form-sections']],
    prefix: '/admin/ministries',
  },
  {
    articles: [a.branding, a['church-settings'], a['guest-experience']],
    prefix: '/admin/settings/branding',
  },
  {
    articles: [
      a['church-settings'],
      a.branding,
      a.automations,
      a['team-management'],
    ],
    prefix: '/admin/settings',
  },
  {
    articles: [a['team-management'], a.profile, a['getting-started']],
    prefix: '/admin/team',
  },

  // Account & Billing
  {
    articles: [
      a.billing,
      a['getting-started'],
      a.profile,
      a['checkin-sections'],
    ],
    prefix: '/admin/billing',
  },
  {
    articles: [a.profile, a.billing, a['team-management']],
    prefix: '/admin/profile',
  },
  {
    articles: [a['getting-started'], a.billing, a.classes],
    prefix: '/admin/create-account',
  },
  // Fallback for any other admin page
  {
    articles: [
      a['getting-started'],
      a.classes,
      a['onark-support-index'],
      a.billing,
    ],
    prefix: '/admin',
  },
];

/**
 * Returns the Beacon suggestion list for the given pathname,
 * filtering out any empty IDs (not yet populated).
 *
 * Handles section builder pages specially by detecting the section type
 * from the last URL segment (e.g. /admin/classes/abc/def/Video → video-sections).
 */
export function getArticlesForRoute(pathname: string): string[] {
  // Course automations: /admin/classes/[courseId]/automations
  if (/^\/admin\/classes\/[^/]+\/automations$/.test(pathname)) {
    return [a.automations, a['branch-sections'], a['church-settings']].filter(
      Boolean
    );
  }

  // Section builder: /admin/classes/[courseId]/[sectionId]/SectionType
  const sectionBuilderMatch = /^\/admin\/classes\/[^/]+\/[^/]+\/(\w+)$/.exec(
    pathname
  );

  if (sectionBuilderMatch) {
    const sectionType = sectionBuilderMatch[1];
    const articles = SECTION_TYPE_ARTICLES[sectionType];

    if (articles) return articles.filter(Boolean);
  }

  const match = [...ROUTE_ARTICLES]
    .sort((a, b) => b.prefix.length - a.prefix.length)
    .find(r => pathname.startsWith(r.prefix));

  return (match?.articles ?? []).filter(Boolean);
}
